import { z } from "zod";
import {
  firstNameField,
  lastNameField,
  emailField,
  pronounsField,
  websiteField,
  bioField,
  timezoneField,
  emailNotificationsField,
  textNotificationsField,
  photoField,
  cvField,
  currentPasswordField,
  passwordField,
  confirmPasswordField,
  phoneNumberField,
  confirmPhoneNumberField,
} from "@core/schemas/fields";
import {
  withPasswordConfirmation,
  withPhoneConfirmation,
} from "@core/schemas/refinements";

/**
 * Profile schema fields
 */
export const profileSchemaFields = {
  firstName: firstNameField,
  lastName: lastNameField,
  photo: photoField,
  pronouns: pronounsField,
  website: websiteField,
  bio: bioField,
  timezone: timezoneField,
  cv: cvField,
  isEmailNotificationsEnabled: emailNotificationsField,
};

/**
 * Account schema fields (for email change)
 * Requires current password to confirm email change
 */
export const accountSchemaFields = {
  email: emailField,
  password: z
    .string()
    .min(
      8,
      "Enter your current password to confirm changing your email address."
    ),
};

/**
 * Update password schema fields
 */
export const updatePasswordSchemaFields = {
  currentPassword: currentPasswordField,
  password: passwordField,
  confirmPassword: confirmPasswordField,
};

/**
 * Profile form schema
 */
export const baseProfileSchema = z.object(profileSchemaFields);

/**
 * Account form schema (for email change)
 */
export const baseAccountSchema = z.object(accountSchemaFields);

/**
 * Update password form schema with password confirmation validation
 * Note: This uses withPasswordConfirmation which loses currentPassword in type inference
 */
export const baseUpdatePasswordSchema = withPasswordConfirmation(
  z.object(updatePasswordSchemaFields)
);

/**
 * Update password form schema with proper type inference
 * Use this when you need TypeScript to correctly infer all fields including currentPassword
 */
export const updatePasswordSchema = z
  .object(updatePasswordSchemaFields)
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

/**
 * Notification settings schema fields
 */
export const notificationSettingsSchemaFields = {
  isEmailNotificationsEnabled: emailNotificationsField,
  isAnnouncementEmailEnabled: emailNotificationsField,
  isTextNotificationsEnabled: textNotificationsField,
};

/**
 * Timezone schema fields
 */
export const timezoneSchemaFields = {
  timezone: timezoneField,
};

/**
 * Additional info schema fields
 */
export const additionalInfoSchemaFields = {
  cv: cvField,
};

/**
 * Account with phone schema fields
 */
export const accountWithPhoneSchemaFields = {
  email: emailField,
  phoneNumber: phoneNumberField,
  password: z
    .string()
    .min(8, "Enter your current password to confirm changes."),
};

/**
 * Notification settings form schema
 */
export const notificationSettingsSchema = z.object(
  notificationSettingsSchemaFields
);

/**
 * Timezone form schema
 */
export const timezoneSchema = z.object(timezoneSchemaFields);

/**
 * Additional info form schema
 */
export const additionalInfoSchema = z.object(additionalInfoSchemaFields);

/**
 * Account with phone form schema
 */
export const accountWithPhoneSchema = z.object(accountWithPhoneSchemaFields);

/**
 * Phone collection schema fields (for onboarding phone collection step)
 */
export const phoneCollectionSchemaFields = {
  phoneNumber: phoneNumberField,
  confirmPhoneNumber: confirmPhoneNumberField,
  isTextNotificationsEnabled: textNotificationsField,
};

/**
 * Phone collection form schema with phone confirmation validation
 */
export const phoneCollectionSchema = withPhoneConfirmation(
  z.object(phoneCollectionSchemaFields)
);
